<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<script>
import ThumbnailSkeleton from '@src/components/dicom-web/ThumbnailSkeleton.vue';
import { useCurrentStudyStore } from '@src/store/dicom-web/current-study-store';
import { computed, defineComponent, ref } from 'vue';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

export default defineComponent({
  name: 'SeriesThumbnailsOverlay',
  components: {
    ThumbnailSkeleton,
  },
  setup() {
    const isDragging = ref(false);
    const currentStudyStore = useCurrentStudyStore();
    const isFetching = computed(
      () => currentStudyStore.isInitializingThumbnails,
    );
    const thumbnails = computed(() =>
      Object.entries(currentStudyStore.getSeriesThumbnails)
        .sort(([a], [b]) => Number.parseInt(a, 10) - Number.parseInt(b, 10)) // sort
        .map(([, value]) => value),
    );

    const seriesDescriptions = computed(() =>
      Object.entries(currentStudyStore.getSeriesThumbnailDescriptions)
        .sort(([a], [b]) => Number.parseInt(a, 10) - Number.parseInt(b, 10)) // sort
        .map(([, value]) => value),
    );

    const seriesDimensions = computed(() =>
      Object.entries(currentStudyStore.getSeriesDimensions)
        .sort(([a], [b]) => Number.parseInt(a, 10) - Number.parseInt(b, 10)) // sort
        .map(([, value]) => value),
    );

    const thumbnailClicked = (index) => {
      const viewportStateStore = useViewportStateStore();
      const generalViewerViewID = 'AnalysisGeneralView';
      const indices = {
        seriesIdx: index,
        sliceIdx: 0,
        imageIdx: 0,
      };

      viewportStateStore.setSelectedImageIndices(generalViewerViewID, indices);
    };

    const startDrag = (event, index) => {
      isDragging.value = true;

      event.dataTransfer.dropEffect = 'move';

      event.dataTransfer.effectAllowed = 'move';
      const indices = {
        seriesIdx: index,
        sliceIdx: 0,
        imageIdx: 0,
      };
      event.dataTransfer.setData('ImageIndices', JSON.stringify(indices));
    };
    return {
      isFetching,
      thumbnails,
      seriesDescriptions,
      seriesDimensions,
      startDrag,
      thumbnailClicked,
    };
  },
});
</script>

<template>
  <div class="flex flex-col w-full gap-2">
    <div>
      <h5>Series:</h5>
    </div>
    <div class="thumbnails">
      <ThumbnailSkeleton v-if="isFetching" />
      <div
        v-for="(thumbnail, index) in thumbnails"
        :key="index"
        class="thumbnail"
        :class="{ loading: isFetching }"
      >
        <div
          draggable="true"
          @dragstart="startDrag($event, index)"
          @click="thumbnailClicked(index)"
        >
          <div class="small-thumbnail-image">
            <img
              :id="index"
              :src="thumbnail"
              class="w-full h-full object-cover"
              :alt="`Thumbnail ${index + 1}`"
            >
            <div class="series-chip simple-panel px-2 py-1">
              <strong>{{ index + 1 }}</strong> ({{
                seriesDimensions[index].sliceCount
              }}/{{ seriesDimensions[index].phaseCount }})
            </div>
          </div>
        </div>
        <div class="break-words" style="width: var(--small-thumbnail-width)">
          <p>
            {{ seriesDescriptions[index] || '(no series description)' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.thumbnails {
  display: grid;
  justify-content: center;
  align-content: flex-start;
  height: 100%;
  overflow: auto;
  grid-template-columns: repeat(auto-fill, minmax(var(--small-thumbnail-width), 1fr));
  row-gap: 10px;
  gap: 5px;
}

.loading {
  opacity: 0;
}

.thumbnail {
  cursor: pointer;
}

.thumbnail:hover {
  filter: brightness(120%);
}

.small-thumbnail-image {
  position: relative;
}

.series-chip {
  position: absolute;
  bottom: 6px;
  right: 6px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 1;
  pointer-events: none;
}
</style>
